import React, { useEffect, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import { X, Loader2, Copy, Check, Repeat, CheckCircle2, AlertTriangle, RefreshCw } from 'lucide-react';
import { Negociacao, UnidadeConsumidora } from '../../../../types';
import { useToast } from '../../../../src/contexts/ToastContext';
import { supabase } from '../../../../src/lib/supabase';
import { useAuth } from '../../../../src/contexts/AuthContext';
import { logUsageEvent } from '../../../../src/lib/logger';
import { propsFundoModal } from '../../../../src/lib/backdropModal';

/** Por que o modal foi aberto — decide quais UCs já vêm marcadas.
 *  - enviar: primeiro envio ou reenvio de quem ainda não assinou
 *  - outraUc: o cliente já tem documento numa UC e falta mandar o das outras
 *  - corrigir: a distribuidora recusou um documento já assinado */
export type ModoTrocaTitularidade = 'enviar' | 'outraUc' | 'corrigir';

interface TrocaTitularidadeModalProps {
    isOpen: boolean;
    onClose: () => void;
    negotiation: Negociacao;
    ucs: UnidadeConsumidora[];
    onGerado: () => void;
    modo?: ModoTrocaTitularidade;
}

const DOCUMENTOS_OPTIONS = [
    'RG / RANI', 'CPF', 'CNH', 'CTPS', 'Passaporte', 'Contrato de locação',
    'Doc. Posse/Propriedade/Usufruto', 'Procuração', 'Doc. Representação', 'Certidão de óbito', 'Outros',
];
const MOTIVO_OPTIONS = ['Arrendamento', 'Locação', 'Compra/Venda', 'Falecimento', 'Cessão', 'Outros'];
const VINCULO_OPTIONS = ['Sem vínculo', 'Cônjuge', 'Parentesco', 'Inquilino', 'Comprador', 'Outros'];
const STATUS_FORNECIMENTO_OPTIONS = ['Ligado', 'Suspenso', 'Desligado'];
const ATIVIDADE_OPTIONS = ['Residencial', 'Industrial', 'Rural', 'Comercial'];

/** Pausa entre as chamadas do lote.
 *
 * O caminho de ESCRITA do Autentique (createDocument) não tem throttle nenhum, e a
 * API responde "Too Many Attempts." sob rajada — ver o incidente documentado em
 * supabase/functions/_shared/autentique.ts (uma varredura sem pausa perdeu 48 de 66
 * registros). scripts/enviar-troca-titularidade-lote.cjs usa este mesmo intervalo. */
const INTERVALO_ENTRE_ENVIOS_MS = 1000;

const dormir = (ms: number) => new Promise(resolve => setTimeout(resolve, ms));

const composeEndereco = (uc?: UnidadeConsumidora): string => {
    if (!uc) return '';
    const cidadeUf = uc.cidade ? `${uc.cidade}${uc.estado ? `/${uc.estado}` : ''}` : '';
    const partes = [uc.logradouro, uc.numero, uc.bairro, cidadeUf].filter(Boolean);
    if (partes.length > 0) return partes.join(', ');
    return uc.endereco || '';
};

/** Sugestão simples pela faixa do grupo tarifário — o vendedor confirma/ajusta antes de enviar. */
const sugerirAtividade = (grupoTarifario?: string): string => {
    if (!grupoTarifario) return '';
    if (grupoTarifario === 'B1') return 'Residencial';
    if (grupoTarifario === 'B3') return 'Comercial';
    if (grupoTarifario.startsWith('A')) return 'Industrial';
    return '';
};

/** Campos que descrevem a LIGAÇÃO — variam de uma UC para outra. */
interface CamposUC {
    contaContratoNova: string;
    unidadeConsumidora: string;
    parceiroNegocio: string;
    enderecoLigacao: string;
    leituraAtualMedidor: string;
    statusFornecimento: string;
    atividadeLocal: string;
    atividadeRamo: string;
    serviceRequest: string;
}

const camposIniciais = (uc: UnidadeConsumidora): CamposUC => ({
    contaContratoNova: uc.numero_uc || '',
    unidadeConsumidora: uc.outro_numero_uc || uc.numero_uc || '',
    parceiroNegocio: uc.num_parceiro || '',
    enderecoLigacao: composeEndereco(uc),
    leituraAtualMedidor: '',
    statusFornecimento: '',
    atividadeLocal: sugerirAtividade(uc.grupo_tarifario),
    atividadeRamo: '',
    serviceRequest: '',
});

interface ResultadoUC {
    ucId: number;
    ucLabel: string;
    link?: string;
    numero?: string | null;
    erro?: string;
}

/** Último documento gerado para a UC — é ele que vale para o selo da lista. */
interface SituacaoUC {
    assinadoEm: string | null;
}

const formatarDiaMes = (iso: string) =>
    new Date(iso).toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit' });

/** UCs que já vêm marcadas ao abrir, conforme o motivo da abertura. Se a regra não
 *  sobrar nenhuma, marca todas — melhor que abrir o modal com o botão travado. */
const selecaoInicial = (
    ucs: UnidadeConsumidora[],
    situacao: Map<number, SituacaoUC>,
    modo: ModoTrocaTitularidade
): number[] => {
    const filtro = (uc: UnidadeConsumidora) => {
        const s = situacao.get(uc.id);
        if (modo === 'outraUc') return !s;
        if (modo === 'corrigir') return !!s?.assinadoEm;
        return !s?.assinadoEm;
    };
    const ids = ucs.filter(filtro).map(u => u.id);
    return ids.length > 0 ? ids : ucs.map(u => u.id);
};

const inputCls = "w-full border border-gray-200 dark:border-slate-700 rounded-lg px-3 py-2 text-sm focus:border-primary outline-none bg-white dark:bg-slate-900 dark:text-slate-100";
const labelCls = "block text-xs font-bold text-gray-500 dark:text-slate-400 mb-1 uppercase tracking-wider";
const sectionTitleCls = "text-xs font-bold text-gray-500 dark:text-slate-400 uppercase tracking-wider mb-2";

/**
 * Gera a Declaração de Troca de Titularidade (Equatorial) para as UCs selecionadas
 * de uma negociação que a Análise Cadastral sinalizou como dependente disso.
 *
 * Um cliente pode ter várias UCs e cada uma precisa da SUA declaração — o formulário
 * oficial tem um único campo "Unidade Consumidora", desenhado no PDF. Por isso o
 * envio é um documento por UC, cada um por uma chamada própria à Edge Function:
 * 1 chamada = 1 UC = 1 PDF = 1 POST no webhook = 1 documento no Autentique = 1 log.
 * Uma falha no meio do lote não derruba as UCs já enviadas.
 *
 * Campos alinhados com o formulário real: os que descrevem a PESSOA (nome, CPF,
 * motivo, vínculo, documentos) são únicos; os que descrevem a LIGAÇÃO (UC, conta
 * contrato, endereço, leitura, atividade) são por UC, pré-preenchidos do cadastro
 * de cada uma e editáveis individualmente.
 *
 * Só o cliente assina — sem procurador/testemunhas, diferente de Aditivo/Advertência/Mora.
 */
const TrocaTitularidadeModal: React.FC<TrocaTitularidadeModalProps> = ({ isOpen, onClose, negotiation, ucs, onGerado, modo = 'enviar' }) => {
    const { addToast } = useToast();
    const { user } = useAuth();
    const [loading, setLoading] = useState(false);
    const [progresso, setProgresso] = useState<{ atual: number; total: number } | null>(null);
    const [resultados, setResultados] = useState<ResultadoUC[] | null>(null);
    const [copiedKey, setCopiedKey] = useState<string | null>(null);

    // Ordem estável: o embed do PostgREST não garante ordem, e a lista mudar de posição
    // entre aberturas confunde quem está conferindo UC por UC.
    const ucsOrdenadas = useMemo(
        () => [...ucs].sort((a, b) => (a.id ?? 0) - (b.id ?? 0)),
        [ucs]
    );
    const primeiraUc = ucsOrdenadas[0];

    const [selectedIds, setSelectedIds] = useState<number[]>([]);
    const [camposPorUc, setCamposPorUc] = useState<Record<number, CamposUC>>({});
    // Documentos já gerados, por UC. Enquanto carrega, o envio fica travado: a
    // seleção inicial depende de saber quais UCs já têm documento.
    const [situacaoPorUc, setSituacaoPorUc] = useState<Map<number, SituacaoUC>>(new Map());
    const [carregandoEnvios, setCarregandoEnvios] = useState(false);

    // Campos do SOLICITANTE — valem para todos os documentos do lote
    const [nomeSolicitante, setNomeSolicitante] = useState('');
    const [cpfCnpj, setCpfCnpj] = useState('');
    const [contato, setContato] = useState('');
    const [nisBpc, setNisBpc] = useState('');
    const [documentosApresentados, setDocumentosApresentados] = useState<string[]>([]);
    const [motivo, setMotivo] = useState('');
    const [vinculoClienteAnterior, setVinculoClienteAnterior] = useState('');
    const [consentimentoLgpd, setConsentimentoLgpd] = useState(false);

    const isReenvio = negotiation.troca_titularidade_status === 'documento_enviado'
        || negotiation.troca_titularidade_status === 'aguardando_reanalise';

    useEffect(() => {
        if (!isOpen) return;

        const carregarDadosPadrao = () => {
            setSelectedIds([]);
            setSituacaoPorUc(new Map());
            setCamposPorUc(Object.fromEntries(ucsOrdenadas.map(u => [u.id, camposIniciais(u)])));
            // Dados da pessoa: a negociação manda, a UC é fallback (todas as UCs da
            // negociação são do mesmo cliente).
            setNomeSolicitante(negotiation.cliente_nome || primeiraUc?.nome_cliente || '');
            setCpfCnpj(negotiation.cpf_cnpj || primeiraUc?.cpf_cnpj_titular || primeiraUc?.cpf_cnpj_cliente || '');
            setContato(negotiation.whatsapp || negotiation.email || primeiraUc?.whatsapp_cliente || primeiraUc?.email_cliente || '');
            setNisBpc('');
            setDocumentosApresentados([]);
            setMotivo('');
            setVinculoClienteAnterior('');
            setConsentimentoLgpd(false);
        };

        setResultados(null);
        setProgresso(null);
        carregarDadosPadrao();

        // Busca TODOS os documentos já gerados para a negociação, por dois motivos:
        //  1. Saber o que cada UC já tem (selo na lista + quais UCs vêm marcadas). Um cliente
        //     com várias UCs pode trocar a titularidade de uma hoje e de outra amanhã.
        //  2. Reaproveitar o que foi preenchido antes (ex.: o campo que causou a rejeição no
        //     Autentique), em vez de o vendedor redigitar Motivo/Vínculo/Atividade.
        // Usa o mais recente DE CADA UC: com várias UCs, pegar só o último log da negociação
        // preencheria todas com os dados de uma UC só.
        setCarregandoEnvios(true);
        (async () => {
            try {
                const { data: logs } = await supabase
                    .from('termo_troca_titularidade_logs')
                    .select('uc_id, dados_documento, created_at, assinado_em')
                    .eq('negociacao_id', negotiation.id)
                    .order('created_at', { ascending: false });

                // Como `logs` vem em ordem decrescente, o primeiro que aparece para cada
                // uc_id é o mais recente.
                const situacao = new Map<number, SituacaoUC>();
                for (const log of logs || []) {
                    const id = (log as any).uc_id as number | null;
                    if (id != null && !situacao.has(id)) {
                        situacao.set(id, { assinadoEm: (log as any).assinado_em ?? null });
                    }
                }
                setSituacaoPorUc(situacao);
                setSelectedIds(selecaoInicial(ucsOrdenadas, situacao, modo));

                if (!logs || logs.length === 0) return;

                // Campos da pessoa: o envio mais recente, qualquer que seja a UC.
                const maisRecente = (logs[0]?.dados_documento || {}) as Record<string, any>;
                if (maisRecente.nome_solicitante) setNomeSolicitante(maisRecente.nome_solicitante);
                if (maisRecente.cpf_cnpj) setCpfCnpj(maisRecente.cpf_cnpj);
                if (maisRecente.nis_bpc) setNisBpc(maisRecente.nis_bpc);
                if (maisRecente.documentos_apresentados) setDocumentosApresentados(maisRecente.documentos_apresentados);
                // Motivo e vínculo dependem de quem é o titular atual DAQUELA UC (numa o cliente
                // é inquilino, noutra a conta está no nome do filho). Para outra UC, ficam em
                // branco e o vendedor escolhe — copiar do envio anterior assinaria dado errado.
                if (modo !== 'outraUc') {
                    if (maisRecente.motivo) setMotivo(maisRecente.motivo);
                    if (maisRecente.vinculo_cliente_anterior) setVinculoClienteAnterior(maisRecente.vinculo_cliente_anterior);
                }
                if (typeof maisRecente.consentimento_lgpd_marketing === 'boolean') {
                    setConsentimentoLgpd(maisRecente.consentimento_lgpd_marketing);
                }

                // Campos da ligação: cada UC recebe o seu próprio último envio.
                const ultimoPorUc = new Map<number, Record<string, any>>();
                for (const log of logs) {
                    const id = (log as any).uc_id as number | null;
                    if (id != null && !ultimoPorUc.has(id)) {
                        ultimoPorUc.set(id, ((log as any).dados_documento || {}) as Record<string, any>);
                    }
                }
                if (ultimoPorUc.size === 0) return;

                setCamposPorUc(prev => {
                    const proximo = { ...prev };
                    ultimoPorUc.forEach((dd, ucId) => {
                        const atual = proximo[ucId];
                        if (!atual) return; // log de uma UC que não está mais na negociação
                        proximo[ucId] = {
                            ...atual,
                            contaContratoNova: dd.conta_contrato_nova || atual.contaContratoNova,
                            unidadeConsumidora: dd.unidade_consumidora || atual.unidadeConsumidora,
                            parceiroNegocio: dd.parceiro_negocio || atual.parceiroNegocio,
                            enderecoLigacao: dd.endereco_ligacao || atual.enderecoLigacao,
                            leituraAtualMedidor: dd.leitura_atual_medidor || atual.leituraAtualMedidor,
                            statusFornecimento: dd.status_fornecimento || atual.statusFornecimento,
                            atividadeLocal: dd.atividade_local || atual.atividadeLocal,
                            atividadeRamo: dd.atividade_ramo || atual.atividadeRamo,
                            serviceRequest: dd.service_request || atual.serviceRequest,
                        };
                    });
                    return proximo;
                });
            } finally {
                setCarregandoEnvios(false);
            }
        })();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isOpen]);

    if (!isOpen) return null;

    const toggleDocumento = (doc: string) => {
        setDocumentosApresentados(prev => prev.includes(doc) ? prev.filter(d => d !== doc) : [...prev, doc]);
    };

    const toggleUc = (id: number) => {
        if (loading) return;
        setSelectedIds(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]);
    };

    const setCampo = (ucId: number, campo: keyof CamposUC, valor: string) => {
        setCamposPorUc(prev => ({ ...prev, [ucId]: { ...prev[ucId], [campo]: valor } }));
    };

    const rotuloUc = (uc: UnidadeConsumidora) =>
        camposPorUc[uc.id]?.unidadeConsumidora || uc.numero_uc || `UC ${uc.id}`;

    const ucsSelecionadas = ucsOrdenadas.filter(u => selectedIds.includes(u.id));

    /** Envia um documento por UC, sequencialmente. `apenasIds` reenvia só as que falharam. */
    const enviarLote = async (apenasIds?: number[]) => {
        const alvo = apenasIds
            ? ucsOrdenadas.filter(u => apenasIds.includes(u.id))
            : ucsSelecionadas;

        if (alvo.length === 0) {
            addToast('Selecione ao menos uma Unidade Consumidora.', 'warning');
            return;
        }
        if (!motivo || !vinculoClienteAnterior) {
            addToast('Preencha Motivo e Vínculo com o titular anterior.', 'warning');
            return;
        }
        const semAtividade = alvo.filter(u => !camposPorUc[u.id]?.atividadeLocal);
        if (semAtividade.length > 0) {
            addToast(
                `Preencha "Atividade exercida no local" da(s) UC(s): ${semAtividade.map(rotuloUc).join(', ')}.`,
                'warning'
            );
            return;
        }

        setLoading(true);
        const novos: ResultadoUC[] = [];

        for (let i = 0; i < alvo.length; i++) {
            // Só entre chamadas — não atrasa a primeira.
            if (i > 0) await dormir(INTERVALO_ENTRE_ENVIOS_MS);

            const uc = alvo[i];
            const campos = camposPorUc[uc.id];
            setProgresso({ atual: i + 1, total: alvo.length });

            try {
                const { data, error } = await supabase.functions.invoke('gerar-troca-titularidade', {
                    body: {
                        negociacao_id: negotiation.id,
                        uc_id: uc.id,
                        dados_documento: {
                            nome_solicitante: nomeSolicitante,
                            cpf_cnpj: cpfCnpj,
                            unidade_consumidora: campos.unidadeConsumidora,
                            parceiro_negocio: campos.parceiroNegocio,
                            service_request: campos.serviceRequest,
                            nis_bpc: nisBpc,
                            documentos_apresentados: documentosApresentados,
                            status_fornecimento: campos.statusFornecimento,
                            leitura_atual_medidor: campos.leituraAtualMedidor,
                            motivo,
                            vinculo_cliente_anterior: vinculoClienteAnterior,
                            atividade_local: campos.atividadeLocal,
                            atividade_ramo: campos.atividadeRamo,
                            consentimento_lgpd_marketing: consentimentoLgpd,
                            endereco_ligacao: campos.enderecoLigacao,
                        },
                    },
                });
                if (error) throw error;
                if (!data?.success) throw new Error(data?.error || 'Erro ao gerar o documento.');

                novos.push({
                    ucId: uc.id,
                    ucLabel: rotuloUc(uc),
                    link: data.link_assinatura_cliente,
                    numero: data.numero_termo || null,
                });
            } catch (error: any) {
                console.error(`Erro ao gerar Troca de Titularidade da UC ${rotuloUc(uc)}:`, error);
                // Não aborta o lote: as UCs seguintes ainda podem ser enviadas, e o guard de
                // status da Edge Function aceita 'documento_enviado', então retentar é seguro.
                novos.push({ ucId: uc.id, ucLabel: rotuloUc(uc), erro: error.message || 'Erro desconhecido' });
            }
        }

        setProgresso(null);
        setLoading(false);

        // Mescla com o que já havia (caso de retentativa parcial).
        setResultados(prev => {
            if (!prev) return novos;
            const porId = new Map(prev.map(r => [r.ucId, r]));
            novos.forEach(r => porId.set(r.ucId, r));
            return ucsOrdenadas.filter(u => porId.has(u.id)).map(u => porId.get(u.id)!);
        });

        const ok = novos.filter(r => r.link).length;
        const falhas = novos.length - ok;
        if (ok > 0) logUsageEvent({ userId: user?.id, modulo: 'crm', funcionalidade: 'funil', acao: 'gerar_troca_titularidade', origem: 'acao' });
        if (ok > 0) onGerado();
        if (falhas > 0) {
            addToast(`${ok} documento(s) enviado(s), ${falhas} falhou(ram).`, ok > 0 ? 'warning' : 'error');
        }
    };

    const copiar = (texto: string, chave: string) => {
        navigator.clipboard.writeText(texto);
        setCopiedKey(chave);
        setTimeout(() => setCopiedKey(null), 2000);
    };

    const handleClose = () => {
        setResultados(null);
        setProgresso(null);
        onClose();
    };

    const sucessos = resultados?.filter(r => r.link) || [];
    const falhas = resultados?.filter(r => r.erro) || [];

    const titulo = modo === 'outraUc'
        ? 'Troca de Titularidade de outra UC'
        : isReenvio ? 'Reenviar Troca de Titularidade' : 'Troca de Titularidade';
    // Correção pós-assinatura (ex.: Equatorial recusou por endereço sem complemento) — o
    // aviso de "já assinado" já apareceu antes de abrir este modal (ConfirmModal em
    // DetailLeftColumn), aqui só reforça que o link antigo será substituído.
    const subtitulo = modo === 'outraUc'
        ? 'Já vêm marcadas as UCs que ainda não têm documento. As que já foram enviadas não mudam.'
        : modo === 'corrigir'
            ? 'O cliente já havia assinado — gera um novo documento/link e ele precisará assinar de novo. O documento anterior fica registrado no histórico.'
            : isReenvio
                ? 'Gera novos documentos e novos links — revise os dados antes de enviar.'
                : 'Declaração de Troca de Titularidade (Equatorial) — uma por UC.';

    return createPortal(
        <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/50 backdrop-blur-sm p-4 animate-fade-in" {...propsFundoModal(() => handleClose())}>
            <div className="bg-white dark:bg-slate-900 rounded-2xl shadow-2xl w-full max-w-2xl overflow-hidden flex flex-col max-h-[90vh]" onClick={e => e.stopPropagation()}>

                {/* Header */}
                <div className="flex items-center justify-between p-4 md:p-6 border-b border-gray-100 dark:border-slate-700/50 bg-gray-50/50 dark:bg-slate-800/50">
                    <div className="flex items-center gap-3">
                        <div className="p-2 bg-primary/10 text-primary rounded-lg border border-primary/20">
                            <Repeat size={20} />
                        </div>
                        <div>
                            <h2 className="text-lg font-bold text-gray-800 dark:text-slate-100 font-poppins">
                                {titulo}
                            </h2>
                            <p className="text-sm text-gray-500 dark:text-slate-400">
                                {subtitulo}
                            </p>
                        </div>
                    </div>
                    <button
                        onClick={handleClose}
                        className="p-2 text-gray-400 dark:text-slate-500 hover:text-gray-600 dark:hover:text-slate-300 hover:bg-gray-100 dark:hover:bg-slate-800 rounded-lg transition-colors"
                    >
                        <X size={20} />
                    </button>
                </div>

                {/* Body */}
                <div className="flex-1 overflow-y-auto p-4 md:p-6">
                    {resultados ? (
                        <div className="space-y-4">
                            <div className="flex flex-col items-center text-center space-y-2">
                                <div className={`w-16 h-16 rounded-full flex items-center justify-center ${falhas.length === 0 ? 'bg-green-100 dark:bg-green-900/30 text-green-500' : 'bg-amber-100 dark:bg-amber-900/30 text-amber-500'}`}>
                                    {falhas.length === 0 ? <Check size={32} /> : <AlertTriangle size={32} />}
                                </div>
                                <h3 className="text-xl font-bold text-gray-900 dark:text-slate-100">
                                    {falhas.length === 0
                                        ? `${sucessos.length} documento(s) enviado(s)!`
                                        : `${sucessos.length} enviado(s), ${falhas.length} com erro`}
                                </h3>
                                <p className="text-sm text-gray-500 dark:text-slate-400 max-w-sm">
                                    Cada UC tem seu próprio link de assinatura. Assim que o cliente assinar o primeiro,
                                    o cadastro volta automaticamente para a fila de reanálise.
                                </p>
                            </div>

                            {sucessos.length > 1 && (
                                <button
                                    onClick={() => copiar(sucessos.map(r => `UC ${r.ucLabel}: ${r.link}`).join('\n'), 'todos')}
                                    className="w-full flex items-center justify-center gap-1.5 px-3 py-2 bg-white dark:bg-slate-900 border border-gray-200 dark:border-slate-600 rounded-lg text-sm text-gray-700 dark:text-slate-200 hover:bg-gray-50 dark:hover:bg-slate-800 transition-colors shadow-sm"
                                >
                                    {copiedKey === 'todos' ? <Check size={14} className="text-green-500" /> : <Copy size={14} />}
                                    {copiedKey === 'todos' ? 'Copiado!' : 'Copiar todos os links'}
                                </button>
                            )}

                            <div className="space-y-2">
                                {resultados.map(r => (
                                    <div
                                        key={r.ucId}
                                        className={`p-3 rounded-xl border ${r.erro
                                            ? 'bg-red-50 dark:bg-red-900/20 border-red-200 dark:border-red-800/50'
                                            : 'bg-gray-50 dark:bg-slate-800 border-gray-200 dark:border-slate-700'}`}
                                    >
                                        <div className="flex items-center justify-between gap-3 mb-1">
                                            <span className="text-sm font-bold text-gray-800 dark:text-slate-100">UC {r.ucLabel}</span>
                                            {r.numero && (
                                                <span className="text-xs text-gray-400 dark:text-slate-500 font-mono">{r.numero}</span>
                                            )}
                                        </div>
                                        {r.erro ? (
                                            <p className="text-xs text-red-600 dark:text-red-400">{r.erro}</p>
                                        ) : (
                                            <div className="flex items-center justify-between gap-3">
                                                <span className="text-xs text-gray-600 dark:text-slate-300 truncate">{r.link}</span>
                                                <button
                                                    onClick={() => copiar(r.link!, String(r.ucId))}
                                                    className="flex-shrink-0 flex items-center gap-1.5 px-3 py-1.5 bg-white dark:bg-slate-900 border border-gray-200 dark:border-slate-600 rounded-lg text-xs text-gray-700 dark:text-slate-200 hover:bg-gray-50 dark:hover:bg-slate-800 transition-colors shadow-sm"
                                                >
                                                    {copiedKey === String(r.ucId) ? <Check size={12} className="text-green-500" /> : <Copy size={12} />}
                                                    {copiedKey === String(r.ucId) ? 'Copiado!' : 'Copiar'}
                                                </button>
                                            </div>
                                        )}
                                    </div>
                                ))}
                            </div>

                            {falhas.length > 0 && (
                                <button
                                    onClick={() => enviarLote(falhas.map(r => r.ucId))}
                                    disabled={loading}
                                    className="w-full flex items-center justify-center gap-2 px-4 py-2 bg-primary hover:opacity-90 text-white rounded-xl text-sm font-bold transition-colors shadow-sm disabled:opacity-70"
                                >
                                    {loading ? <Loader2 size={16} className="animate-spin" /> : <RefreshCw size={16} />}
                                    {loading && progresso
                                        ? `Reenviando ${progresso.atual} de ${progresso.total}...`
                                        : `Tentar novamente as ${falhas.length} que falharam`}
                                </button>
                            )}
                        </div>
                    ) : (
                        <div className="space-y-6">
                            {/* Seleção de UCs */}
                            <div>
                                <h3 className={sectionTitleCls}>
                                    Unidades Consumidoras — {selectedIds.length} de {ucsOrdenadas.length} selecionada(s)
                                </h3>
                                <p className="text-xs text-gray-400 dark:text-slate-500 mb-2">
                                    Cada UC marcada gera uma declaração e um link de assinatura próprios.
                                </p>
                                {carregandoEnvios && (
                                    <p className="flex items-center gap-2 text-xs text-gray-400 dark:text-slate-500 mb-2">
                                        <Loader2 size={12} className="animate-spin" />
                                        Conferindo quais UCs já têm documento...
                                    </p>
                                )}
                                <div className="space-y-2">
                                    {ucsOrdenadas.map(uc => {
                                        const marcada = selectedIds.includes(uc.id);
                                        const situacao = situacaoPorUc.get(uc.id);
                                        return (
                                            <div
                                                key={uc.id}
                                                onClick={() => toggleUc(uc.id)}
                                                className={`relative border rounded-xl p-3 transition-all cursor-pointer flex items-start gap-3 hover:border-primary/30 ${marcada
                                                    ? 'bg-primary/5 border-primary/40'
                                                    : 'bg-white dark:bg-slate-900 border-gray-100 dark:border-slate-700/50'}`}
                                            >
                                                <div className={`mt-0.5 flex-shrink-0 w-5 h-5 rounded border flex items-center justify-center transition-colors ${marcada
                                                    ? 'bg-primary border-primary text-white'
                                                    : 'border-gray-300 dark:border-slate-600 bg-white dark:bg-slate-800'}`}>
                                                    {marcada && <CheckCircle2 size={12} />}
                                                </div>
                                                <div className="min-w-0 flex-1">
                                                    <div className="flex items-center justify-between gap-2">
                                                        <p className="text-sm font-bold text-gray-800 dark:text-slate-100 truncate">
                                                            {uc.numero_uc || `UC ${uc.id}`}
                                                        </p>
                                                        {situacao?.assinadoEm ? (
                                                            <span className="flex-shrink-0 text-[11px] font-semibold px-2 py-0.5 rounded-full bg-teal-50 dark:bg-teal-900/20 text-teal-700 dark:text-teal-400 border border-teal-200 dark:border-teal-800/50">
                                                                Assinada em {formatarDiaMes(situacao.assinadoEm)}
                                                            </span>
                                                        ) : situacao ? (
                                                            <span className="flex-shrink-0 text-[11px] font-semibold px-2 py-0.5 rounded-full bg-amber-50 dark:bg-amber-900/20 text-amber-700 dark:text-amber-400 border border-amber-200 dark:border-amber-800/50">
                                                                Aguardando assinatura
                                                            </span>
                                                        ) : null}
                                                    </div>
                                                    <p className="text-xs text-gray-500 dark:text-slate-400 truncate">
                                                        {[uc.nome_cliente, uc.cidade, uc.situacao].filter(Boolean).join(' · ')}
                                                    </p>
                                                    {marcada && situacao?.assinadoEm && modo !== 'corrigir' && (
                                                        <p className="text-xs text-amber-700 dark:text-amber-400 mt-1">
                                                            Esta UC já foi assinada. Marcada, gera um documento novo e o cliente assina de novo.
                                                        </p>
                                                    )}
                                                </div>
                                            </div>
                                        );
                                    })}
                                </div>
                            </div>

                            {/* Dados do solicitante — comuns a todos os documentos */}
                            <div>
                                <h3 className={sectionTitleCls}>Dados do solicitante (valem para todas as UCs)</h3>
                                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                                    <div className="sm:col-span-2">
                                        <label className={labelCls}>Nome do Solicitante</label>
                                        <input type="text" value={nomeSolicitante} onChange={e => setNomeSolicitante(e.target.value)} className={inputCls} />
                                    </div>
                                    <div>
                                        <label className={labelCls}>CPF / CNPJ</label>
                                        <input type="text" value={cpfCnpj} onChange={e => setCpfCnpj(e.target.value)} className={inputCls} />
                                    </div>
                                    <div>
                                        <label className={labelCls}>Contato (telefone ou e-mail)</label>
                                        <input type="text" value={contato} onChange={e => setContato(e.target.value)} className={inputCls} />
                                    </div>
                                    <div>
                                        <label className={labelCls}>Motivo *</label>
                                        <select value={motivo} onChange={e => setMotivo(e.target.value)} className={inputCls}>
                                            <option value="">Selecione...</option>
                                            {MOTIVO_OPTIONS.map(o => <option key={o} value={o}>{o}</option>)}
                                        </select>
                                    </div>
                                    <div>
                                        <label className={labelCls}>Vínculo com o titular anterior *</label>
                                        <select value={vinculoClienteAnterior} onChange={e => setVinculoClienteAnterior(e.target.value)} className={inputCls}>
                                            <option value="">Selecione...</option>
                                            {VINCULO_OPTIONS.map(o => <option key={o} value={o}>{o}</option>)}
                                        </select>
                                    </div>
                                    <div>
                                        <label className={labelCls}>NIS / BPC</label>
                                        <input type="text" value={nisBpc} onChange={e => setNisBpc(e.target.value)} className={inputCls} placeholder="Opcional" />
                                    </div>
                                </div>
                            </div>

                            {/* Dados da ligação — um bloco por UC selecionada */}
                            {ucsSelecionadas.length > 0 && (
                                <div>
                                    <h3 className={sectionTitleCls}>Dados da ligação (por UC)</h3>
                                    <div className="space-y-4">
                                        {ucsSelecionadas.map(uc => {
                                            const campos = camposPorUc[uc.id];
                                            if (!campos) return null;
                                            return (
                                                <div key={uc.id} className="border border-gray-200 dark:border-slate-700 rounded-xl p-3">
                                                    <p className="text-sm font-bold text-gray-800 dark:text-slate-100 mb-3">
                                                        UC {uc.numero_uc || uc.id}
                                                    </p>
                                                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                                                        <div>
                                                            <label className={labelCls}>Conta Contrato Nova</label>
                                                            <input type="text" value={campos.contaContratoNova} onChange={e => setCampo(uc.id, 'contaContratoNova', e.target.value)} className={inputCls} />
                                                        </div>
                                                        <div>
                                                            <label className={labelCls}>Unidade Consumidora</label>
                                                            <input type="text" value={campos.unidadeConsumidora} onChange={e => setCampo(uc.id, 'unidadeConsumidora', e.target.value)} className={inputCls} />
                                                        </div>
                                                        <div>
                                                            <label className={labelCls}>Parceiro de Negócio</label>
                                                            <input type="text" value={campos.parceiroNegocio} onChange={e => setCampo(uc.id, 'parceiroNegocio', e.target.value)} className={inputCls} />
                                                        </div>
                                                        <div>
                                                            <label className={labelCls}>Service Request</label>
                                                            <input type="text" value={campos.serviceRequest} onChange={e => setCampo(uc.id, 'serviceRequest', e.target.value)} className={inputCls} placeholder="Opcional" />
                                                        </div>
                                                        <div className="sm:col-span-2">
                                                            <label className={labelCls}>Endereço da Ligação</label>
                                                            <input type="text" value={campos.enderecoLigacao} onChange={e => setCampo(uc.id, 'enderecoLigacao', e.target.value)} className={inputCls} />
                                                        </div>
                                                        <div>
                                                            <label className={labelCls}>Atividade exercida no local *</label>
                                                            <select value={campos.atividadeLocal} onChange={e => setCampo(uc.id, 'atividadeLocal', e.target.value)} className={inputCls}>
                                                                <option value="">Selecione...</option>
                                                                {ATIVIDADE_OPTIONS.map(o => <option key={o} value={o}>{o}</option>)}
                                                            </select>
                                                        </div>
                                                        {campos.atividadeLocal === 'Comercial' && (
                                                            <div>
                                                                <label className={labelCls}>Ramo (Comercial)</label>
                                                                <input type="text" value={campos.atividadeRamo} onChange={e => setCampo(uc.id, 'atividadeRamo', e.target.value)} className={inputCls} placeholder="Ex: varejo, alimentação..." />
                                                            </div>
                                                        )}
                                                        <div>
                                                            <label className={labelCls}>Status do fornecimento</label>
                                                            <select value={campos.statusFornecimento} onChange={e => setCampo(uc.id, 'statusFornecimento', e.target.value)} className={inputCls}>
                                                                <option value="">Selecione...</option>
                                                                {STATUS_FORNECIMENTO_OPTIONS.map(o => <option key={o} value={o}>{o}</option>)}
                                                            </select>
                                                        </div>
                                                        <div>
                                                            <label className={labelCls}>Leitura Atual do Medidor</label>
                                                            <input type="text" value={campos.leituraAtualMedidor} onChange={e => setCampo(uc.id, 'leituraAtualMedidor', e.target.value)} className={inputCls} />
                                                        </div>
                                                    </div>
                                                </div>
                                            );
                                        })}
                                    </div>
                                </div>
                            )}

                            {/* Validação Cadastral — documentos apresentados */}
                            <div>
                                <h3 className={sectionTitleCls}>Documentos apresentados</h3>
                                <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                                    {DOCUMENTOS_OPTIONS.map(doc => (
                                        <label key={doc} className="flex items-center gap-2 text-sm text-gray-700 dark:text-slate-300 cursor-pointer">
                                            <input
                                                type="checkbox"
                                                checked={documentosApresentados.includes(doc)}
                                                onChange={() => toggleDocumento(doc)}
                                                className="rounded border-gray-300 dark:border-slate-600 text-primary focus:ring-primary/40"
                                            />
                                            {doc}
                                        </label>
                                    ))}
                                </div>
                            </div>

                            {/* LGPD */}
                            <div className="bg-gray-50 dark:bg-slate-800/60 border border-gray-200 dark:border-slate-700 rounded-xl p-3">
                                <label className="flex items-start gap-2.5 text-sm text-gray-700 dark:text-slate-300 cursor-pointer">
                                    <input
                                        type="checkbox"
                                        checked={consentimentoLgpd}
                                        onChange={e => setConsentimentoLgpd(e.target.checked)}
                                        className="mt-0.5 rounded border-gray-300 dark:border-slate-600 text-primary focus:ring-primary/40"
                                    />
                                    <span>
                                        Cliente consente em receber ofertas de seguros e serviços financeiros do Grupo Equatorial Energia (LGPD).
                                        <span className="block text-xs text-gray-400 dark:text-slate-500 mt-0.5">Desmarcado por padrão — só marque com confirmação explícita do cliente.</span>
                                    </span>
                                </label>
                            </div>

                            <p className="text-xs text-gray-400 dark:text-slate-500">
                                Só o cliente assina este documento (assinatura eletrônica + validação facial via Autentique,
                                mesmo fluxo do Termo de Adesão).
                            </p>
                        </div>
                    )}
                </div>

                {/* Footer */}
                {!resultados && (
                    <div className="p-4 border-t border-gray-100 dark:border-slate-700/50 bg-gray-50 dark:bg-slate-800/80 flex justify-end gap-3">
                        <button
                            type="button"
                            onClick={handleClose}
                            disabled={loading}
                            className="px-4 py-2 bg-white dark:bg-slate-900 border border-gray-200 dark:border-slate-700 text-gray-700 dark:text-slate-200 rounded-xl text-sm font-semibold hover:bg-gray-50 dark:hover:bg-slate-800 transition-colors shadow-sm disabled:opacity-50"
                        >
                            Cancelar
                        </button>
                        <button
                            type="button"
                            onClick={() => enviarLote()}
                            disabled={loading || carregandoEnvios || selectedIds.length === 0}
                            className="px-6 py-2 bg-primary hover:opacity-90 text-white rounded-xl text-sm font-bold transition-colors shadow-sm disabled:opacity-70 flex items-center justify-center gap-2 min-w-[190px]"
                        >
                            {loading && <Loader2 size={16} className="animate-spin" />}
                            {loading && progresso
                                ? `Gerando ${progresso.atual} de ${progresso.total}...`
                                : selectedIds.length === 1
                                    ? 'Gerar e Enviar'
                                    : `Gerar e Enviar ${selectedIds.length} documentos`}
                        </button>
                    </div>
                )}
            </div>
        </div>,
        document.body
    );
};

export default TrocaTitularidadeModal;
